import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { gsap } from "gsap";
import cmsPageAPI from "../../api/cmsPageApi";
import SEO from "../../components/SEO";


const PAGE_CONFIG = {
  "privacy-policy": {
    eyebrow: "Your Privacy Matters",
    title: "Privacy",
    titleEm: "Policy",
    meta: "Last updated: January 2025 · Effective immediately",
    acceptanceBanner: null,
    watermarkType: "circles",
    staticSections: [
      {
        id: "collection", num: "01", title: "Information We <em>Collect</em>",
        body: [
          { type: "text", text: "At FATIMA CREATION, we collect information that helps us serve you with the care and attention your patronage deserves. This includes personal information you provide directly, and certain technical data gathered automatically." },
          { type: "highlight", text: "We collect only what is necessary — and nothing more. Your trust is the fabric of our relationship." },
          { type: "list", items: ["Full name, email address, phone number, and delivery address", "Payment information (processed securely; we do not store card details)", "Order history, preferences, and bespoke customisation choices", "Device type, browser, IP address, and pages visited on our platform", "Communications you share with our team via email or contact forms"] },
        ],
      },
      {
        id: "use", num: "02", title: "How We <em>Use</em> Your Information",
        body: [
          { type: "text", text: "Your information enables us to fulfil your orders with precision, personalise your FATIMA CREATION experience, and keep you informed about collections, events, and exclusive offers that match your taste." },
          { type: "list", items: ["Process and deliver your orders, including bespoke requests", "Send order confirmations, shipping updates, and invoices", "Respond to your enquiries with the attention they deserve", "Improve our platform, collections, and services", "Send newsletters and curated recommendations (only with your consent)", "Comply with legal obligations and prevent fraudulent activity"] },
        ],
      },
      {
        id: "sharing", num: "03", title: "Sharing of <em>Information</em>",
        body: [
          { type: "text", text: "FATIMA CREATION does not sell, rent, or trade your personal information. We share your data only where necessary to deliver our services, and always under strict confidentiality obligations." },
          { type: "list", items: ["Logistics and courier partners for order delivery", "Secure payment gateways for transaction processing", "Email and communication service providers", "Legal or regulatory authorities when required by law"] },
          { type: "text", text: "All third-party partners are carefully selected and bound by data protection agreements aligned with applicable law." },
        ],
      },
      {
        id: "cookies", num: "04", title: "Cookies & <em>Tracking</em>",
        body: [
          { type: "text", text: "We use cookies and similar technologies to enhance your browsing experience, remember your preferences, and understand how our platform is used. You may manage cookie preferences through your browser settings." },
          { type: "highlight", text: "Essential cookies are required for the platform to function. Analytical and preference cookies can be declined without affecting your ability to shop." },
          { type: "list", items: ["Session cookies: temporary, deleted when you close your browser", "Preference cookies: remember your language, region, and settings", "Analytics cookies: help us understand site usage (anonymised)", "Marketing cookies: show you relevant content (only with consent)"] },
        ],
      },
      {
        id: "rights", num: "05", title: "Your <em>Rights</em>",
        body: [
          { type: "text", text: "You hold the right to access, correct, or request deletion of your personal information at any time. You may also withdraw consent for marketing communications, request data portability, or object to certain types of processing." },
          { type: "list", items: ["Access a copy of the personal data we hold about you", "Correct inaccurate or outdated information", "Request deletion of your data (subject to legal obligations)", "Withdraw consent for marketing communications at any time", "Lodge a complaint with the relevant data protection authority"] },
          { type: "text", text: "To exercise any of these rights, please write to us at privacy@fatimacreation.in. We will respond within 30 days." },
        ],
      },
      {
        id: "security", num: "06", title: "Data <em>Security</em>",
        body: [
          { type: "text", text: "We employ industry-standard security measures including SSL encryption, secure server infrastructure, and access controls to protect your personal information from unauthorised access, disclosure, or misuse." },
          { type: "text", text: "While no digital system can guarantee absolute security, we continuously review and strengthen our safeguards to maintain the trust you place in us." },
        ],
      },
      {
        id: "contact", num: "07", title: "Contact Our <em>Privacy Team</em>",
        body: [
          { type: "text", text: "If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please do not hesitate to reach out." },
          { type: "contact", name: "FATIMA CREATION Privacy Team", email: "privacy@fatimacreation.in", note: "Response time: within 30 business days" },
        ],
      },
    ],
  },

  "terms-and-conditions": {
    eyebrow: "Please Read Carefully",
    title: "Terms &",
    titleEm: "Conditions",
    meta: "Effective: January 2025 · Governing law: India",
    acceptanceBanner: "By using the FATIMA CREATION platform, you agree to these terms.",
    watermarkType: "polygons",
    introBox: {
      label: "Welcome to FATIMA CREATION",
      text: "These Terms and Conditions govern your use of the FATIMA CREATION platform and all purchases made therein. FATIMA CREATION is a curated ethnic wear brand rooted in the heritage of Indian artisanal craft. We are committed to fair, transparent, and respectful commerce.",
    },
    staticSections: [
      {
        id: "acceptance", num: "01", title: "Acceptance of <em>Terms</em>",
        body: [
          { type: "text", text: "By accessing the FATIMA CREATION platform, placing an order, or using any of our services, you confirm that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree, please refrain from using our services." },
          { type: "highlight", text: "Use of FATIMA CREATION constitutes acceptance of these terms. We recommend saving or printing a copy for your records." },
        ],
      },
      {
        id: "products", num: "02", title: "Products & <em>Bespoke Orders</em>",
        body: [
          { type: "text", text: "FATIMA CREATION offers a curated range of ethnic and artisanal wear, including bespoke creations crafted to your specifications. All product descriptions, images, and measurements are provided in good faith and may vary slightly due to the handcrafted nature of our garments." },
          { type: "list", items: ["Colours may vary slightly from on-screen representation due to fabric dye lots and monitor calibration", "Bespoke orders are non-returnable once production has commenced", "Size guides are advisory; we recommend consulting our atelier for accurate measurements", "Embellishments and embroidery placements may have minor variations — this is the character of handcraft"] },
        ],
      },
      {
        id: "ordering", num: "03", title: "Ordering & <em>Payment</em>",
        body: [
          { type: "text", text: "All orders placed on FATIMA CREATION are subject to availability and acceptance. We reserve the right to cancel or refuse any order at our discretion, with a full refund where applicable." },
          { type: "table", headers: ["Method", "Processing Time", "Currencies"], rows: [["Credit / Debit Card", "Instant", "INR, USD, GBP"], ["UPI & Net Banking", "Instant", "INR"], ["Bank Transfer (Bespoke)", "2–3 Business Days", "INR"], ["EMI (select banks)", "Instant", "INR"]] },
          { type: "text", text: "Prices are inclusive of applicable taxes unless stated otherwise. International orders may be subject to customs duties levied by the destination country — these are the buyer's responsibility." },
        ],
      },
      {
        id: "shipping", num: "04", title: "Shipping & <em>Delivery</em>",
        body: [
          { type: "list", items: ["Standard delivery within India: 5–7 business days", "Express delivery within India: 2–3 business days", "International shipping: 10–15 business days", "Bespoke orders: 21–45 business days from confirmation of final design", "Orders above ₹5,000 qualify for complimentary standard shipping within India"] },
          { type: "text", text: "FATIMA CREATION is not liable for delays caused by courier partners, customs clearance, or events beyond our control." },
        ],
      },
      {
        id: "returns", num: "05", title: "Returns & <em>Exchanges</em>",
        body: [
          { type: "highlight", text: "We stand behind the quality of every piece that leaves our atelier. If something is not right, we will make it right." },
          { type: "list", items: ["Ready-to-wear items may be returned within 14 days of delivery in original, unworn condition with tags intact", "Bespoke, customised, and altered garments are non-returnable", "Sale and discounted items are final sale and not eligible for return", "Items showing signs of wear, washing, or damage will not be accepted"] },
          { type: "text", text: "To initiate a return, contact us at returns@fatimacreation.in with your order number and reason for return within the return window." },
        ],
      },
      {
        id: "ip", num: "06", title: "Intellectual <em>Property</em>",
        body: [
          { type: "text", text: "All content on the FATIMA CREATION platform — including designs, photographs, brand identity, copy, and digital assets — is the exclusive property of FATIMA CREATION and protected under applicable intellectual property laws." },
          { type: "text", text: "You may not reproduce, distribute, modify, or use any FATIMA CREATION content for commercial purposes without express written permission." },
        ],
      },
      {
        id: "governing", num: "07", title: "Governing <em>Law</em>",
        body: [
          { type: "text", text: "These Terms and Conditions are governed by and construed in accordance with the laws of India. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the courts of New Delhi." },
        ],
      },
      {
        id: "contact", num: "08", title: "Contact <em>Us</em>",
        body: [
          { type: "text", text: "For any questions regarding these Terms and Conditions, or for legal notices, please contact our team:" },
          { type: "contact", name: "FATIMA CREATION Legal & Customer Care", email: "legal@fatimacreation.in", note: "Response time: within 5–7 business days for legal queries" },
        ],
      },
    ],
  },

  "return-and-refund-policy": {
    eyebrow: "Hassle-Free Returns",
    title: "Return &",
    titleEm: "Refund Policy",
    meta: "Effective: January 2025 · Subject to terms",
    acceptanceBanner: null,
    watermarkType: "circles",
    staticSections: [
      {
        id: "eligibility", num: "01", title: "Return <em>Eligibility</em>",
        body: [
          { type: "highlight", text: "We stand behind the quality of every piece that leaves our atelier. If something is not right, we will make it right." },
          { type: "list", items: ["Ready-to-wear items may be returned within 14 days of delivery", "Items must be in original, unworn condition with all tags intact", "Bespoke, customised, and altered garments are non-returnable", "Sale and discounted items are final sale — not eligible for return", "Items showing signs of wear, washing, or damage will not be accepted"] },
        ],
      },
      {
        id: "process", num: "02", title: "Return <em>Process</em>",
        body: [
          { type: "text", text: "To initiate a return, contact our team within the return window with your order number and reason for return. We will guide you through the next steps." },
          { type: "list", items: ["Email returns@fatimacreation.in with your order number and reason", "Our team will respond within 2 business days with return instructions", "Pack the item securely in its original packaging wherever possible", "Ship the item using a tracked courier service", "Refund will be processed within 7–10 business days of receiving the item"] },
        ],
      },
      {
        id: "refunds", num: "03", title: "Refund <em>Methods</em>",
        body: [
          { type: "text", text: "Approved refunds are processed to the original payment method. In cases where the original method is unavailable, a FATIMA CREATION store credit will be issued." },
          { type: "table", headers: ["Payment Method", "Refund Timeline", "Mode"], rows: [["Credit / Debit Card", "5–7 Business Days", "Original card"], ["UPI / Net Banking", "3–5 Business Days", "Original account"], ["Store Credit", "Instant", "FATIMA CREATION wallet"]] },
        ],
      },
      {
        id: "exchanges", num: "04", title: "Exchanges & <em>Store Credit</em>",
        body: [
          { type: "text", text: "Exchanges are subject to stock availability. If your preferred size or colour is unavailable, we will issue a store credit that never expires and can be used across all FATIMA CREATION collections." },
          { type: "highlight", text: "FATIMA CREATION store credit never expires. Use it whenever you are ready — on any collection, any season." },
        ],
      },
      {
        id: "contact", num: "05", title: "Contact Our <em>Returns Team</em>",
        body: [
          { type: "text", text: "For all return and refund queries, reach out to us directly and we'll take care of the rest." },
          { type: "contact", name: "FATIMA CREATION Returns Team", email: "returns@fatimacreation.in", note: "Response time: within 2 business days" },
        ],
      },
    ],
  },
};

// Default fallback for unknown routes
const DEFAULT_CONFIG = {
  eyebrow: "Legal Information",
  title: "Our",
  titleEm: "Policy",
  meta: "Effective: January 2025",
  acceptanceBanner: null,
  watermarkType: "circles",
  staticSections: [],
};

// ─────────────────────────────────────────────
//  Watermark SVGs
// ─────────────────────────────────────────────
const WatermarkCircles = ({ className }) => (
  <svg className={className} viewBox="0 0 400 400" fill="none">
    <circle cx="200" cy="200" r="190" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="140" stroke="white" strokeWidth="0.5" />
    <circle cx="200" cy="200" r="90" stroke="white" strokeWidth="1" />
    {[0,45,90,135,180,225,270,315].map((a, i) => {
      const r = (a * Math.PI) / 180;
      return <circle key={i} cx={200+140*Math.cos(r)} cy={200+140*Math.sin(r)} r="6" stroke="white" strokeWidth="0.8" />;
    })}
    {[0,30,60,90,120,150,180,210,240,270,300,330].map((a, i) => {
      const r = (a * Math.PI) / 180;
      return <line key={i} x1={200+40*Math.cos(r)} y1={200+40*Math.sin(r)} x2={200+190*Math.cos(r)} y2={200+190*Math.sin(r)} stroke="white" strokeWidth="0.4" />;
    })}
  </svg>
);

const WatermarkPolygons = ({ className }) => (
  <svg className={className} viewBox="0 0 400 400" fill="none">
    <circle cx="200" cy="200" r="190" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="140" stroke="white" strokeWidth="0.5" />
    <circle cx="200" cy="200" r="90" stroke="white" strokeWidth="1" />
    {[0,60,120,180,240,300].map((a, i) => {
      const r = (a * Math.PI) / 180;
      return <polygon key={i}
        points={`${200+140*Math.cos(r)},${200+140*Math.sin(r)} ${200+155*Math.cos(r+0.15)},${200+155*Math.sin(r+0.15)} ${200+155*Math.cos(r-0.15)},${200+155*Math.sin(r-0.15)}`}
        stroke="white" strokeWidth="0.8" fill="none" />;
    })}
    {[0,30,60,90,120,150,180,210,240,270,300,330].map((a, i) => {
      const r = (a * Math.PI) / 180;
      return <line key={i} x1={200+40*Math.cos(r)} y1={200+40*Math.sin(r)} x2={200+190*Math.cos(r)} y2={200+190*Math.sin(r)} stroke="white" strokeWidth="0.4" />;
    })}
  </svg>
);

// ─────────────────────────────────────────────
//  Styles
// ─────────────────────────────────────────────


// ─────────────────────────────────────────────
//  Helper: render a single content block
// ─────────────────────────────────────────────
function RenderBlock({ block, i, variant }) {
  // variant: "simple" (privacy/refund) | "tnc" (terms)
  if (block.type === "text")
    return <p key={i} className="cms-text">{block.text}</p>;

  if (block.type === "highlight")
    return <div key={i} className="cms-highlight">{block.text}</div>;

  if (block.type === "list")
    return (
      <ul key={i} className="cms-list">
        {block.items.map((item, j) => <li key={j}>{item}</li>)}
      </ul>
    );

  if (block.type === "table")
    return (
      <div key={i} className="cms-table-wrap">
        <table className="cms-table">
          <thead><tr>{block.headers.map((h, j) => <th key={j}>{h}</th>)}</tr></thead>
          <tbody>
            {block.rows.map((row, j) => (
              <tr key={j}>{row.map((cell, k) => <td key={k}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  if (block.type === "contact")
    return (
      <div key={i} className="cms-contact-box">
        <div className="cms-contact-box-title">{block.name}</div>
        <p className="cms-contact-box-text">
          47, Craft Lane, Lajpat Nagar, New Delhi — 110024<br />
          {block.note}
        </p>
        <a href={`mailto:${block.email}`} className="cms-contact-box-email">{block.email}</a>
      </div>
    );

  return null;
}

// ─────────────────────────────────────────────
//  Loading skeleton
// ─────────────────────────────────────────────
const LoadingSkeleton = () => (
  <div>
    {[1, 2, 3].map(i => (
      <div key={i} className="cms-section">
        <div className="cms-skeleton short" style={{ marginBottom: "0.5rem" }} />
        <div className="cms-skeleton medium" style={{ height: "1.4rem", marginBottom: "1.2rem" }} />
        <div className="cms-skeleton wide" />
        <div className="cms-skeleton wide" />
        <div className="cms-skeleton medium" />
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────
export default function TermsofServices() {
  const location = useLocation();
  const path = location.pathname.split("/")[1]; // e.g. "privacy-policy"

  // Pick config based on current route
  const config = PAGE_CONFIG[path] || DEFAULT_CONFIG;
  const isTerms = path === "terms-and-conditions";

  // API state
  const [metaData, setMetaData]     = useState({});
  const [cmsContent, setCmsContent] = useState("");
  const [loading, setLoading]       = useState(true);

  // UI state
  const firstSectionId = config.staticSections?.[0]?.id || "";
  const [activeId, setActiveId]     = useState(firstSectionId);

  // Refs for GSAP
  const heroEyebrowRef  = useRef(null);
  const heroTitleRef    = useRef(null);
  const heroRuleRef     = useRef(null);
  const heroMetaRef     = useRef(null);
  const acceptanceRef   = useRef(null);
  const tocRef          = useRef(null);
  const contentRef      = useRef(null);

  // Re-fetch when route changes
  useEffect(() => {
    setLoading(true);
    setCmsContent("");
    setActiveId(config.staticSections?.[0]?.id || "");
    fetchCMSDetails();
  }, [path]);

  const fetchCMSDetails = async () => {
    try {
      const res = await cmsPageAPI.details(path);
      if (res.code === 200) {
        const result = res.data;
        setCmsContent(result.content || "");
        setMetaData(result?.meta || {});
      }
    } catch (err) {
      // fall back to static sections silently
    } finally {
      setLoading(false);
    }
  };

  // GSAP entrance animation
  useEffect(() => {
    if (typeof gsap === "undefined") return;
    const targets = [
      [heroEyebrowRef.current,  { opacity: 1, duration: 0.6 },              0.1],
      [heroTitleRef.current,    { opacity: 1, y: 0, duration: 0.8 },        0.25],
      [heroRuleRef.current,     { opacity: 1, scaleX: 1, duration: 0.6 },   0.5],
      [heroMetaRef.current,     { opacity: 1, duration: 0.5 },              0.65],
      [acceptanceRef.current,   { opacity: 1, duration: 0.5 },              0.75],
      [tocRef.current,          { opacity: 1, x: 0, duration: 0.7 },        0.85],
      [contentRef.current,      { opacity: 1, y: 0, duration: 0.7 },        1.0],
    ];
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    targets.forEach(([el, props, pos]) => {
      if (el) tl.to(el, props, pos);
    });
  }, [path]); // re-run on route change

  const scrollToSection = (id) => {
    setActiveId(id);
    document.getElementById(`cms-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const hasCmsContent = !loading && cmsContent && cmsContent.trim().length > 0;
  const Watermark = config.watermarkType === "polygons" ? WatermarkPolygons : WatermarkCircles;

  return (
    <>
      <SEO meta={metaData} />

      <div className="cms-page">

        {/* ── Hero ── */}
        <div className="cms-hero">
          <div className="cms-hero-bg" />
          <Watermark className="cms-hero-watermark" />
          <p className="cms-hero-eyebrow" ref={heroEyebrowRef}>{config.eyebrow}</p>
          <h1 className="cms-hero-title" ref={heroTitleRef}>
            {config.title} <em>{config.titleEm}</em>
          </h1>
          <div className="cms-hero-rule" ref={heroRuleRef} />
          <p className="cms-hero-meta" ref={heroMetaRef}>{config.meta}</p>
        </div>

        {/* ── Acceptance Banner (T&C only) ── */}
        {config.acceptanceBanner && (
          <div className="cms-acceptance" ref={acceptanceRef}>
            <p>
              {config.acceptanceBanner}&nbsp;
              <strong>If you do not agree, please discontinue use.</strong>
            </p>
          </div>
        )}

        {/* invisible ref when no banner, so GSAP doesn't error */}
        {!config.acceptanceBanner && <div ref={acceptanceRef} style={{ display: "none" }} />}

        {/* ── Main layout ── */}
        <div className={`cms-layout${hasCmsContent ? " full-width" : ""}`}>

          {/* Sidebar TOC — only in static mode */}
          {!hasCmsContent && config.staticSections.length > 0 ? (
            <nav className="cms-toc" ref={tocRef}>
              <div className="cms-toc-label">Contents</div>
              {config.staticSections.map(s => (
                <button
                  key={s.id}
                  className={`cms-toc-item${activeId === s.id ? " active" : ""}`}
                  onClick={() => scrollToSection(s.id)}
                >
                  {s.num}&nbsp;&nbsp;{s.title.replace(/<[^>]+>/g, "")}
                </button>
              ))}
              <div className="cms-toc-divider" />
              <div className="cms-toc-updated">Last updated<br />January 2025</div>
            </nav>
          ) : (
            <div ref={tocRef} style={{ display: "none" }} />
          )}

          {/* ── Content ── */}
          <div className="cms-content" ref={contentRef}>

            {loading ? (
              <LoadingSkeleton />

            ) : hasCmsContent ? (
              /* API HTML content */
              <div
                className="cms-api-content"
                dangerouslySetInnerHTML={{ __html: cmsContent }}
              />

            ) : (
              /* Static fallback sections */
              <>
                {/* Intro box for T&C */}
                {isTerms && config.introBox && (
                  <div className="cms-intro">
                    <div className="cms-intro-label">{config.introBox.label}</div>
                    <p className="cms-intro-text">{config.introBox.text}</p>
                  </div>
                )}

                {config.staticSections.map(s => (
                  <div key={s.id} id={`cms-${s.id}`} className="cms-section">

                    {/* Header variant */}
                    {isTerms ? (
                      <div className="cms-section-header-flex">
                        <span className="cms-section-num-large">{s.num}</span>
                        <h2
                          className="cms-section-title"
                          dangerouslySetInnerHTML={{ __html: s.title }}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="cms-section-number">{s.num}</div>
                        <h2
                          className="cms-section-title"
                          dangerouslySetInnerHTML={{ __html: s.title }}
                        />
                      </>
                    )}

                    {s.body.map((block, i) => (
                      <RenderBlock key={i} block={block} i={i} variant={isTerms ? "tnc" : "simple"} />
                    ))}
                  </div>
                ))}
              </>
            )}

          </div>
        </div>
      </div>
    </>
  );
}